// BoardView.jsx

import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Utilities
import { BOARD_LAYOUT_LANES, normalizeBoardLayout } from 'utilities/BoardLayout';
import { formatPrice, resolveFieldCurrencyCode } from 'utilities/Currency';
import { getEnumOptionLabel } from 'utilities/EnumOptions';
import { isEnumCompatibleFieldType, normalizeSchemaFieldType } from 'utilities/FieldTypes';
import { formatLocationDisplayValue } from 'utilities/Location';
import { normalizeNodelistKeys } from 'utilities/NodelistField';
import { extractRichTextPlainText } from 'utilities/RichText';
import { isBoardCardEligibleFieldType } from 'views/board/utils/boardCardFields';
import { normalizeBoardOrder } from 'views/board/utils/boardOrder';
import { canPersistViewLayout } from 'views/utils/organicViews';
import { formatTemporalFieldValue, isFirestoreTimestampObject, isTemporalFieldType } from 'views/utils/temporalFieldFormat';

// Services
import { updateNode } from 'services/nodes/NodeWriteService';
import { resolveImageUrl } from 'services/storage/StorageAccessService';
import { updateView } from 'services/views/ViewWriteService';

// Hooks
import useBoardCardDnD from 'views/board/hooks/useBoardCardDnD';
import useBoardGroups, { OTHER_KEY, UNASSIGNED_KEY } from 'views/board/hooks/useBoardGroups';
import useBoardNodelistHydration from 'views/board/hooks/useBoardNodelistHydration';
import useBoardOptimisticFieldOverrides from 'views/board/hooks/useBoardOptimisticFieldOverrides';
import useDefaultNodeOpen from 'views/hooks/useDefaultNodeOpen';
import useTableEditState from 'views/table/hooks/useTableEditState';
import useTableHandlers from 'views/table/hooks/useTableHandlers';
import useTableModalActions from 'views/table/hooks/useTableModalActions';
import useTableModalState from 'views/table/hooks/useTableModalState';

// CSS
import './BoardView.css';

// Components
import { toast } from 'react-toastify';
import BoardLanesView from 'views/board/components/BoardLanesView';
import BoardViewColumn from 'views/board/components/BoardViewColumn';
import TableModals from 'views/table/components/TableModals';
import { getDataSectionName } from 'views/table/utils/tableFieldUtils';

const normalizeOptionOrderIds = (ids) => (
    Array.isArray(ids)
        ? [...new Set(ids
            .map((value) => String(value || '').trim())
            .filter(Boolean))]
        : []
);

const normalizeBoardEnumOptions = (rawOptions, boardFieldKey = '') => {
    const config = rawOptions && typeof rawOptions === 'object' ? rawOptions : {};
    const resolvedFieldKey = String(config.fieldKey || boardFieldKey || '').trim();

    return {
        fieldKey: resolvedFieldKey,
        optionOrderIds: normalizeOptionOrderIds(config.optionOrderIds),
        sortMode: String(config.sortMode || 'inherit').trim().toLowerCase() || 'inherit',
        // No UI manages hidden columns; ignore legacy ids so boards cannot render empty.
        hiddenOptionIds: [],
    };
};

const BOARD_COVER_LOAD_ROOT_MARGIN = '200px';

const BoardViewCardCover = React.memo(({ node, coverUrl }) => {
    // ----- REFS ----- //

    const containerRef = useRef(null);
    // ----- STATE ----- //

    const [shouldLoadCover, setShouldLoadCover] = useState(false);
    const [displayCoverUrl, setDisplayCoverUrl] = useState('');
    const [isCoverLoading, setIsCoverLoading] = useState(false);

    const rawCoverUrl = typeof coverUrl === 'string' ? coverUrl.trim() : '';

    // ----- EFFECTS ----- //

    useEffect(() => {
        setShouldLoadCover(false);
        setDisplayCoverUrl('');
        setIsCoverLoading(false);
    }, [rawCoverUrl, node?.key]);

    useEffect(() => {
        const nodeEl = containerRef.current;
        if (!rawCoverUrl || !nodeEl) {
            return undefined;
        }

        if (typeof IntersectionObserver === 'undefined') {
            setShouldLoadCover(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.target !== nodeEl || !entry.isIntersecting) return;
                    setShouldLoadCover(true);
                });
            },
            {
                root: null,
                rootMargin: BOARD_COVER_LOAD_ROOT_MARGIN,
                threshold: 0,
            },
        );

        observer.observe(nodeEl);

        return () => observer.disconnect();
    }, [rawCoverUrl, node?.key]);

    useEffect(() => {
        if (!shouldLoadCover || !rawCoverUrl) {
            return undefined;
        }

        let cancelled = false;

        setIsCoverLoading(true);
        setDisplayCoverUrl('');

        if (rawCoverUrl.startsWith('blob:') || rawCoverUrl.startsWith('data:')) {
            setDisplayCoverUrl(rawCoverUrl);
            setIsCoverLoading(false);
            return undefined;
        }

        (async () => {
            try {
                const candidate = await resolveImageUrl({
                    nodeKey: node?.key || null,
                    url: rawCoverUrl,
                    storagePath: node?.coverStoragePath || null,
                });
                const finalUrl = (typeof candidate === 'string' && candidate.trim()) ? candidate.trim() : rawCoverUrl;

                const image = new Image();
                image.onload = () => {
                    if (cancelled) return;
                    setDisplayCoverUrl(finalUrl);
                    setIsCoverLoading(false);
                };
                image.onerror = () => {
                    if (cancelled) return;
                    setDisplayCoverUrl('');
                    setIsCoverLoading(false);
                };
                image.src = finalUrl;
            } catch (error) {
                if (!cancelled) {
                    setDisplayCoverUrl('');
                    setIsCoverLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [shouldLoadCover, rawCoverUrl, node?.coverStoragePath, node?.key]);

    if (!rawCoverUrl) return null;

    // ----- RENDER ----- //

    return (
        <div
          className={`board-view-card-cover${isCoverLoading ? ' is-loading' : ''}`}
          ref={containerRef}
          aria-hidden="true"
        >

            {/* CARD COVER PLACEHOLDER */}
            {displayCoverUrl ? (
                <img
                  src={displayCoverUrl}
                  alt=""
                  draggable="false"
                />
            ) : (

                <div className="board-view-card-cover-placeholder" />
            )}
        </div>
    );
});

BoardViewCardCover.displayName = 'BoardViewCardCover';

/**
 * BoardView
 *
 * Renders a kanban-style board grouped by an enum field.
 *
 * @returns {ReactNode} - BoardView.
 */
const BoardView = React.memo(({
    view,
    typeDef,
    nodes = [],
    language = 'en',
    addTypeKey,
    onQuickActionCreateOnly,
    onQuickActionSelect,
    interactionMode = 'base',
    nodeDragEnabled = false,
    onItemClick = null,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    const { user } = useUser();

    // ----- HANDLERS ----- //

    const handleOpenNode = useDefaultNodeOpen();
    const isSurfaceInteraction = interactionMode === 'surface';
    const isPreviewInteraction = interactionMode === 'preview';
    const isReadOnlyInteraction = isSurfaceInteraction || isPreviewInteraction;
    const persistBoardOrder = !isReadOnlyInteraction && canPersistViewLayout(view);

    // ----- STATE ----- //

    // Optimistic state for board order and field values during drag-and-drop

    const [optimisticBoardOrder, setOptimisticBoardOrder] = useState(() => normalizeBoardOrder(view?.boardOrder, view?.boardField || ''));
    const [optimisticBoardEnumOptions, setOptimisticBoardEnumOptions] = useState(() => normalizeBoardEnumOptions(view?.boardEnumOptions, view?.boardField || ''));
    const [optimisticFieldValues, setOptimisticFieldValues] = useState({});
    const [draggingColumnOptionId, setDraggingColumnOptionId] = useState('');
    const [columnDragOverOptionId, setColumnDragOverOptionId] = useState('');

    // ----- MEMOS ----- //

    const effectiveView = useMemo(() => ({
        ...(view || {}),
        boardEnumOptions: optimisticBoardEnumOptions,
    }), [view, optimisticBoardEnumOptions]);

    // ----- HOOKS ----- //

    const {
        boardFieldKey,
        fields,
        boardFieldDef,
        normalizedOptions,
        normalizedBoardOrder,
        boardData,
        resolveFieldValue,
    } = useBoardGroups({
        view: effectiveView,
        typeDef,
        nodes,
        language,
        boardOrder: optimisticBoardOrder,
        fieldValueOverrides: optimisticFieldValues,
    });

    // ----- MEMOS ----- //

    /**
     * boardFieldStorageKey
     *
     * Determines the storage key for the board field's value on nodes, based on the field 
     * definition and type definition. This is used to read/write the field value when moving cards 
     * between columns.
     */

    const boardFieldStorageKey = useMemo(() => {
        const sourceTypeKey = String(boardFieldDef?.source || typeDef?.key || '').trim().toLowerCase();
        if (sourceTypeKey) return `data_${sourceTypeKey}`;
        const typeKey = String(typeDef?.key || '').trim().toLowerCase();
        return typeKey ? `data_${typeKey}` : '';
    }, [boardFieldDef?.source, typeDef?.key]);

    // ----- HOOKS ----- //

    const {
        draggingNodeKey,
        dragOverTarget,
        handleCardDragStart,
        handleCardDragEnd,
        handleInsertDragOver,
        handleInsertDrop,
    } = useBoardCardDnD({
        cancelPendingActionsOpen: () => { },
        optimisticBoardOrder,
        setOptimisticBoardOrder,
        optimisticFieldValues,
        setOptimisticFieldValues,
        boardFieldKey,
        boardData,
        otherKey: OTHER_KEY,
        unassignedKey: UNASSIGNED_KEY,
        nodes,
        actionNode: null,
        boardFieldDef,
        boardFieldStorageKey,
        normalizedBoardOrder,
        viewKey: persistBoardOrder ? view?.key : null,
        persistBoardOrder,
        showStatusSaveToast: isSurfaceInteraction,
        typeDef,
    });

    const safeHandleCardDragStart = isPreviewInteraction ? () => {} : handleCardDragStart;
    const safeHandleCardDragEnd = isPreviewInteraction ? () => {} : handleCardDragEnd;
    const safeHandleInsertDragOver = isPreviewInteraction ? () => {} : handleInsertDragOver;
    const safeHandleInsertDrop = isPreviewInteraction ? () => {} : handleInsertDrop;
    const safeAddTypeKey = isPreviewInteraction ? undefined : addTypeKey;
    const safeOnQuickActionCreateOnly = isPreviewInteraction ? undefined : onQuickActionCreateOnly;
    const safeOnQuickActionSelect = isPreviewInteraction ? undefined : onQuickActionSelect;

    // ----- MEMOS ----- //

    const resetColumnDragState = useCallback(() => {
        setDraggingColumnOptionId('');
        setColumnDragOverOptionId('');
    }, []);

    // ----- HANDLERS ----- //

    const handleColumnDragStart = useCallback((column, event) => {
        if (isReadOnlyInteraction) return;
        const optionId = String(column?.optionId || '').trim();
        if (!optionId) return;

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', optionId);
        setDraggingColumnOptionId(optionId);
        setColumnDragOverOptionId(optionId);
    }, [isReadOnlyInteraction]);

    const handleColumnDragOver = useCallback((column, event) => {
        if (isReadOnlyInteraction) return;
        const optionId = String(column?.optionId || '').trim();
        if (!optionId || !draggingColumnOptionId) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (columnDragOverOptionId !== optionId) {
            setColumnDragOverOptionId(optionId);
        }
    }, [columnDragOverOptionId, draggingColumnOptionId, isReadOnlyInteraction]);

    const handleColumnDrop = useCallback(async (column, event) => {
        if (isReadOnlyInteraction) {
            resetColumnDragState();
            return;
        }
        const targetOptionId = String(column?.optionId || '').trim();
        if (!targetOptionId) {
            resetColumnDragState();
            return;
        }

        event.preventDefault();

        const transferredOptionId = String(event.dataTransfer.getData('text/plain') || draggingColumnOptionId || '').trim();
        if (!transferredOptionId || transferredOptionId === targetOptionId) {
            resetColumnDragState();
            return;
        }

        const currentOptionOrder = boardData.columns
            .map((candidate) => String(candidate?.optionId || '').trim())
            .filter(Boolean);

        const fromIndex = currentOptionOrder.indexOf(transferredOptionId);
        const toIndex = currentOptionOrder.indexOf(targetOptionId);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
            resetColumnDragState();
            return;
        }

        const nextOptionOrder = [...currentOptionOrder];
        const [moved] = nextOptionOrder.splice(fromIndex, 1);
        const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
        nextOptionOrder.splice(insertIndex, 0, moved);

        const previousBoardEnumOptions = optimisticBoardEnumOptions;
        const nextBoardEnumOptions = {
            ...normalizeBoardEnumOptions(previousBoardEnumOptions, boardFieldKey),
            fieldKey: boardFieldKey,
            optionOrderIds: nextOptionOrder,
        };

        setOptimisticBoardEnumOptions(nextBoardEnumOptions);
        resetColumnDragState();

        if (!canPersistViewLayout(view)) {
            return;
        }

        try {
            if (view?.key) {
                await updateView(view.key, {
                    boardEnumOptions: nextBoardEnumOptions,
                });
            }
        } catch (error) {
            setOptimisticBoardEnumOptions(previousBoardEnumOptions);
            toast.error(`Column order could not be saved: ${error?.message || 'Unknown error'}`);
        }
    }, [
        boardData.columns,
        boardFieldKey,
        draggingColumnOptionId,
        optimisticBoardEnumOptions,
        resetColumnDragState,
        view,
        isReadOnlyInteraction,
    ]);

    useBoardOptimisticFieldOverrides({
        nodes,
        boardFieldDef,
        resolveFieldValue,
        setOptimisticFieldValues,
    });

    // ----- MEMOS ----- //

    /**
     * cardFieldKeys
     *
     * Default: all displayable schema fields with values (empty skipped per card).
     * When view.properties is set (organic field chooser / saved view), honor that list.
     * Excludes title/cover, the board group-by field, and checklist fields (JSON on cards).
     */
    const cardFieldKeys = useMemo(() => {
        const excluded = new Set(['title', 'cover', 'coverurl']);
        const groupKey = String(boardFieldKey || '').trim().toLowerCase();
        if (groupKey) excluded.add(groupKey);

        const fieldTypeByKey = new Map();
        (Array.isArray(fields) ? fields : []).forEach((field) => {
            const key = String(field?.name || field?.key || '').trim();
            if (!key) return;
            fieldTypeByKey.set(key.toLowerCase(), field?.type);
        });

        const explicitProperties = Array.isArray(view?.properties)
            ? view.properties.map((key) => String(key || '').trim()).filter(Boolean)
            : null;

        const sourceKeys = explicitProperties !== null
            ? explicitProperties
            : (Array.isArray(fields) ? fields : [])
                .filter((field) => field?.hidden !== true)
                .map((field) => String(field?.name || field?.key || '').trim())
                .filter(Boolean);

        const keys = [];
        const seen = new Set();

        sourceKeys.forEach((key) => {
            const normalized = key.toLowerCase();
            if (!key || excluded.has(normalized) || seen.has(normalized)) return;
            if (!isBoardCardEligibleFieldType(fieldTypeByKey.get(normalized))) return;
            seen.add(normalized);
            keys.push(key);
        });

        if (explicitProperties === null && !seen.has('tags')) {
            keys.push('tags');
        }

        return keys;
    }, [boardFieldKey, fields, view?.properties]);

    /**
     * addButtonLabel
     *
     * Determines the label for the "Add" button on each column, based on the type definition's label or 
     * translations. Falls back to a generic "+ Add" if no specific label is available.
     */
    const addButtonLabel = useMemo(() => {
        const rawLabel = typeDef?.label
            || typeDef?.translations?.en?.singular
            || typeDef?.labelPlural
            || '';
        const trimmed = typeof rawLabel === 'string' ? rawLabel.trim() : '';
        return trimmed ? `+ Add ${trimmed}` : '+ Add';
    }, [typeDef?.label, typeDef?.labelPlural, typeDef?.translations]);

    const schemaFieldMap = useMemo(() => {
        const map = new Map();
        fields.forEach((field) => {
            const key = field?.name || field?.key;
            if (key) map.set(key, field);
        });
        return map;
    }, [fields]);

    const schemaFieldMapLower = useMemo(() => {
        const map = new Map();
        fields.forEach((field) => {
            const key = String(field?.name || field?.key || '').trim().toLowerCase();
            if (key) map.set(key, field);
        });
        return map;
    }, [fields]);

    // Hydrate Relation card fields so titles display instead of raw node keys.
    const { resolveNodelistTitle } = useBoardNodelistHydration({
        nodes,
        schemaFieldMap,
        fieldKeys: cardFieldKeys,
        resolveFieldValue,
    });

    // Lanes cell editing — reuse Table modal + inline editors.
    const fieldDataSection = useMemo(
        () => getDataSectionName(typeDef?.key),
        [typeDef?.key],
    );

    const {
        editingCell,
        editingValue,
        setEditingValue,
        handleStartEdit,
        handleCancelEdit,
        handleCommitEdit,
    } = useTableEditState({
        typeDefKey: typeDef?.key,
        fieldDataSection,
        typeDef,
    });

    const {
        modalState,
        handleModalOpen,
        handleModalClose,
    } = useTableModalState();

    const {
        handleDateChange,
        handleEnumChange,
        handleTagsSaved,
        handleLocationSaved,
        handlePeopleSaved,
        handleNodelistSaved,
        handlePercentageChange,
    } = useTableModalActions({
        modalState,
        typeDefKey: typeDef?.key,
        typeDef,
    });

    const { handleCellActivate } = useTableHandlers({
        openNode: handleOpenNode,
        addTypeKey,
        onQuickActionCreateOnly,
        onQuickActionSelect,
        handleStartEdit: isReadOnlyInteraction ? () => {} : handleStartEdit,
        handleModalOpen: isReadOnlyInteraction ? () => {} : handleModalOpen,
    });

    const effectiveHandleCellActivate = isReadOnlyInteraction
        ? () => {}
        : handleCellActivate;

    const handleNodeFieldChange = useCallback(async (nodeKey, patch) => {
        if (isReadOnlyInteraction || !nodeKey || !patch) return;
        try {
            await updateNode(nodeKey, patch, { actor: user });
        } catch (error) {
            console.error('[BoardView] update field error:', error);
        }
    }, [isReadOnlyInteraction, user]);

    const formatFieldValue = useCallback((value, fieldDef, fieldKey) => {
        if (value == null || value === '') return '';
        const fallbackType = fieldKey === 'dateCreated' || fieldKey === 'dateModified'
            ? 'date'
            : '';
        const fieldType = normalizeSchemaFieldType(fieldDef?.type || fallbackType);
        const isTimestampObject = isFirestoreTimestampObject(value);

        if (fieldType === 'boolean') return value ? 'Yes' : 'No';
        if (isTemporalFieldType(fieldType) || isTimestampObject) {
            return formatTemporalFieldValue({
                value,
                fieldType,
                locale: 'en-US',
                dateOptions: {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                },
                dateTimeOptions: {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                },
            });
        }
        if (isEnumCompatibleFieldType(fieldType)) {
            return getEnumOptionLabel(fieldDef?.options || [], value, language);
        }
        if (fieldType === 'money') {
            if (value == null || value === '') return '';
            return formatPrice(value, resolveFieldCurrencyCode(fieldDef));
        }
        if (fieldType === 'currency') {
            return String(value || '').trim().toUpperCase() || '';
        }
        if (fieldType === 'percentage') {
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) return '';
            const clamped = Math.max(0, Math.min(100, Math.round(numeric)));
            return `${clamped}%`;
        }
        if (fieldType === 'tags') {
            return Array.isArray(value) ? value.join(', ') : String(value);
        }
        if (fieldType === 'richtext') {
            return extractRichTextPlainText(value);
        }
        if (fieldType === 'location') {
            return formatLocationDisplayValue(value);
        }
        if (fieldType === 'relation') {
            const keys = normalizeNodelistKeys(value);
            if (!keys.length) return '';
            return keys
                .map((key) => String(resolveNodelistTitle(key) || '').trim())
                .filter(Boolean)
                .join(', ');
        }
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    }, [language, resolveNodelistTitle]);

    const getCoverUrl = useCallback((node) => {
        if (!node || typeof node?.coverUrl !== 'string') return '';
        const trimmed = node.coverUrl.trim();
        return trimmed.length ? trimmed : '';
    }, []);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setOptimisticBoardOrder(normalizeBoardOrder(view?.boardOrder, view?.boardField || ''));
    }, [view?.key, view?.boardField, view?.boardOrder]);

    useEffect(() => {
        setOptimisticBoardEnumOptions(normalizeBoardEnumOptions(view?.boardEnumOptions, view?.boardField || ''));
    }, [view?.key, view?.boardField, view?.boardEnumOptions]);

    // ----- HANDLERS ----- //

    /**
 * Handles card click.
 *
 * @param {*} event
 * @param {*} node
 */

    const handleCardClick = useCallback((event, node) => {
        event?.preventDefault?.();
        if (typeof onItemClick === 'function') {
            onItemClick(node);
            return;
        }
        handleOpenNode(node);
    }, [handleOpenNode, onItemClick]);

    /**
 * Handles card double click.
 *
 * @param {*} event
 * @param {*} node
 */
    const handleCardDoubleClick = useCallback((event, node) => {
        event?.preventDefault?.();
        if (isReadOnlyInteraction) return;
        if (typeof onItemClick === 'function') {
            return;
        }
        handleOpenNode(node);
    }, [handleOpenNode, isReadOnlyInteraction, onItemClick]);

    /**
 * Handles add for column.
 *
 * @param {*} columnKey
 */
    const handleAddForColumn = useCallback((columnKey) => {
        if (!addTypeKey) return;
        const handler = onQuickActionSelect || onQuickActionCreateOnly;
        if (!handler) return;

        const primaryFieldKey = boardFieldDef?.name || boardFieldDef?.key || boardFieldKey;
        const secondaryFieldKey = boardFieldDef?.key || '';

        const initialFields = (columnKey === UNASSIGNED_KEY || columnKey === OTHER_KEY)
            ? null
            : (() => {
                if (!primaryFieldKey) return null;
                const seeded = { [primaryFieldKey]: columnKey };
                if (secondaryFieldKey && secondaryFieldKey !== primaryFieldKey) {
                    seeded[secondaryFieldKey] = columnKey;
                }
                return seeded;
            })();

        handler(addTypeKey, { initialFields, quickAdd: true, shouldNavigate: false });
    }, [addTypeKey, onQuickActionCreateOnly, onQuickActionSelect, boardFieldDef?.key, boardFieldDef?.name, boardFieldKey]);
    if (!boardFieldKey) {
        return (

            <div
              className="board-view-empty"
            >
                Select a field to group cards by.
            </div>
        );
    }

    if (!boardFieldDef || normalizedOptions.length === 0) {

        return (

            <div
              className="board-view-empty"
            >
                This field has no options yet.
            </div>
        );
    }

    // ----- MEMOS ----- //

    const boardLayout = normalizeBoardLayout(view?.boardLayout);

    // ----- RENDER ----- //

    if (boardLayout === BOARD_LAYOUT_LANES) {
        return (
            <>
                <div className="board-view-container board-view-container-lanes">

                    {/* SCROLL AREA */}
                    <div className="board-view-scroll board-view-scroll-lanes">
                        <BoardLanesView
                          view={view}
                          boardData={boardData}
                          otherKey={OTHER_KEY}
                          dragOverTarget={dragOverTarget}
                          draggingNodeKey={draggingNodeKey}
                          handleInsertDragOver={safeHandleInsertDragOver}
                          handleInsertDrop={safeHandleInsertDrop}
                          handleCardClick={handleCardClick}
                          handleCardDoubleClick={handleCardDoubleClick}
                          handleCardDragStart={safeHandleCardDragStart}
                          handleCardDragEnd={safeHandleCardDragEnd}
                          handleCellActivate={effectiveHandleCellActivate}
                          onNodeChange={handleNodeFieldChange}
                          editingCell={editingCell}
                          editingValue={editingValue}
                          setEditingValue={setEditingValue}
                          handleCommitEdit={isReadOnlyInteraction ? () => {} : handleCommitEdit}
                          handleCancelEdit={handleCancelEdit}
                          language={language}
                          typeKey={typeDef?.key}
                          cardFieldKeys={cardFieldKeys}
                          schemaFieldMap={schemaFieldMap}
                          schemaFieldMapLower={schemaFieldMapLower}
                          resolveFieldValue={resolveFieldValue}
                          formatFieldValue={formatFieldValue}
                          boardFieldKey={boardFieldKey}
                          nodeDragEnabled={nodeDragEnabled && !isPreviewInteraction}
                          addTypeKey={safeAddTypeKey}
                          addButtonLabel={addButtonLabel}
                          handleAddForColumn={handleAddForColumn}
                          isReadOnlyInteraction={isReadOnlyInteraction}
                        />
                    </div>
                </div>

                <TableModals
                  modalState={modalState}
                  fieldDataSection={fieldDataSection}
                  handleModalClose={handleModalClose}
                  handleEnumChange={handleEnumChange}
                  handleDateChange={handleDateChange}
                  handleTagsSaved={handleTagsSaved}
                  handleLocationSaved={handleLocationSaved}
                  handlePeopleSaved={handlePeopleSaved}
                  handleNodelistSaved={handleNodelistSaved}
                  handlePercentageChange={handlePercentageChange}
                  fieldModalOpen={false}
                  handleFieldModalOpen={() => {}}
                  handleFieldSave={() => {}}
                  fieldSaving={false}
                />
            </>
        );
    }

    return (
        <>
            <div className="board-view-container">

            {/* SCROLL AREA */}
            <div className="board-view-scroll">

                {/* COLUMNS */}
                <div className="board-view-columns">
                    {boardData.columns.map((column) => {
                        const items = boardData.groups.get(column.key) || [];
                        return (
                            <BoardViewColumn
                              key={`board_col_${column.key}`}
                              column={column}
                              items={items}
                              otherKey={OTHER_KEY}
                              dragOverTarget={dragOverTarget}
                              theme={theme}
                              handleInsertDragOver={safeHandleInsertDragOver}
                              handleInsertDrop={safeHandleInsertDrop}
                              draggingNodeKey={draggingNodeKey}
                              handleCardClick={handleCardClick}
                              handleCardDoubleClick={handleCardDoubleClick}
                              handleCardDragStart={safeHandleCardDragStart}
                              handleCardDragEnd={safeHandleCardDragEnd}
                              getCoverUrl={getCoverUrl}
                              CoverComponent={BoardViewCardCover}
                              cardFieldKeys={cardFieldKeys}
                              schemaFieldMap={schemaFieldMap}
                              schemaFieldMapLower={schemaFieldMapLower}
                              resolveFieldValue={resolveFieldValue}
                              formatFieldValue={formatFieldValue}
                              language={language}
                              nodeDragEnabled={nodeDragEnabled && !isPreviewInteraction}
                              typeDef={typeDef}
                              addTypeKey={safeAddTypeKey}
                              addButtonLabel={addButtonLabel}
                              handleAddForColumn={handleAddForColumn}
                              isColumnDraggable={Boolean(column?.optionId) && !isReadOnlyInteraction}
                              isColumnDragging={draggingColumnOptionId === column?.optionId}
                              isColumnDropTarget={columnDragOverOptionId === column?.optionId && draggingColumnOptionId !== column?.optionId}
                              handleColumnDragStart={handleColumnDragStart}
                              handleColumnDragOver={handleColumnDragOver}
                              handleColumnDrop={handleColumnDrop}
                              handleColumnDragEnd={resetColumnDragState}
                              onQuickActionCreateOnly={safeOnQuickActionCreateOnly}
                              onQuickActionSelect={safeOnQuickActionSelect}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
        </>
    );

});

// Dev Tools
BoardView.displayName = 'BoardView';

export default BoardView;
